import React, { useState } from 'react';
import { Platform, Pressable, ScrollView, StyleSheet, View, useWindowDimensions } from 'react-native';
import PropTypes from 'prop-types';
import Text from '@/components/Typography/Text';

const PREVIEW_HEIGHT = 76;

/** Keep approval actions outside the bounded details, even when expanded. */
export default function WorkApprovalDetails({ action, detail }) {
  const { height } = useWindowDimensions();
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);
  const body = (
    <View
      style={styles.body}
      onLayout={event => setOverflows(event.nativeEvent.layout.height > PREVIEW_HEIGHT + 1)}
    >
      <Text size="s" color="secondary" style={styles.text}>{action}</Text>
      {!!detail && <Text size="xs" color="tertiary" style={styles.text}>{detail}</Text>}
    </View>
  );
  return (
    <View style={styles.container}>
      {expanded ? (
        <ScrollView
          accessibilityLabel="Approval details"
          style={[styles.expanded, { maxHeight: Math.max(PREVIEW_HEIGHT, Math.min(220, height * 0.25)) }]}
          nestedScrollEnabled
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator
        >
          {body}
        </ScrollView>
      ) : (
        <View style={styles.preview}>{body}</View>
      )}
      {(overflows || expanded) && (
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={expanded ? 'Show less approval detail' : 'Read full approval details'}
          accessibilityState={{ expanded }}
          onPress={() => setExpanded(value => !value)}
          style={styles.toggle}
          hitSlop={6}
        >
          <Text size="xs" weight="medium" color="secondary">{expanded ? 'Show less' : 'Read more'}</Text>
        </Pressable>
      )}
    </View>
  );
}

WorkApprovalDetails.propTypes = {
  action: PropTypes.string,
  detail: PropTypes.string,
};

const styles = StyleSheet.create({
  container: { minWidth: 0, gap: 2 },
  body: { gap: 5, flexShrink: 0 },
  text: Platform.select({ web: { whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }, default: {} }),
  preview: { maxHeight: PREVIEW_HEIGHT, overflow: 'hidden' },
  expanded: { flexGrow: 0, minHeight: 0, ...Platform.select({ web: { overscrollBehavior: 'contain' } }) },
  toggle: { alignSelf: 'flex-start', minHeight: 32, justifyContent: 'center', paddingHorizontal: 2 },
});
